import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  ListItemText,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Search as SearchIcon,
  ShieldOutlined as ShieldIcon,
  Refresh as RefreshIcon,
  EditOutlined as EditIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  LocationOn as LocationIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../apiBase';

const PAGE_OPTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'users', label: 'Users' },
  { key: 'role_master', label: 'Role Master' },
  { key: 'bank_master', label: 'Bank Master' },
  { key: 'page_master', label: 'Page Master' },
  { key: 'vendor_master', label: 'Vendor Master' },
  { key: 'sample2', label: 'Sample Page 2' },
  { key: 'grid', label: 'Grid' },
  { key: 'applications', label: 'Applications' },
  { key: 'edit_requests', label: 'Edit Requests' },
  { key: 'rtgs', label: 'RTGS' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'settings', label: 'Settings' },
];

function RoleChip({ role }) {
  const color =
    role === 'superadmin' ? 'secondary' : role === 'admin' ? 'primary' : 'default';
  return <Chip size="small" label={role} color={color} variant="outlined" />;
}

export default function Users() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [vendorCodes, setVendorCodes] = useState([]);
  const [formApplicationCode, setFormApplicationCode] = useState('');
  const [formMobileNumber, setFormMobileNumber] = useState('');
  const [formName, setFormName] = useState('');
  const [formUsername, setFormUsername] = useState('');
  const [formVehicleNumber, setFormVehicleNumber] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formAssociation, setFormAssociation] = useState([]);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [formRole, setFormRole] = useState('user');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState('success');
  const [toastMessage, setToastMessage] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [userToView, setUserToView] = useState(null);
  const [loginHistoryDialogOpen, setLoginHistoryDialogOpen] = useState(false);
  const [loginHistoryUser, setLoginHistoryUser] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const showToast = (severity, message) => {
    setToastSeverity(severity);
    setToastMessage(message);
    setToastOpen(true);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load users');
      setUsers(data.users || []);
    } catch (e) {
      showToast('error', e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load roles');
      const roles = (data.roles || [])
        .map((r) => String(r.role_name || '').trim())
        .filter(Boolean)
        .filter((r) => r.toLowerCase() !== 'superadmin');
      setRoleOptions(roles);
      if (!roles.map((r) => r.toLowerCase()).includes(String(formRole).toLowerCase())) {
        // prefer 'user' if available, else first role
        const next = roles.find((r) => r.toLowerCase() === 'user') || roles[0] || 'user';
        setFormRole(next);
      }
    } catch (e) {
      // keep fallback
      setRoleOptions(['admin', 'user']);
    }
  };

  const fetchVendorCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/vendor-codes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load vendor codes');
      const raw = Array.isArray(data.vendor_codes) ? data.vendor_codes : [];
      const mapped = raw
        .map((item) => {
          const code = String(item.code || item.firm_vendor_code || '').trim();
          if (!code) return null;
          let bankCodes = [];
          if (Array.isArray(item.bank_codes)) {
            bankCodes = item.bank_codes.map((b) => String(b || '').trim()).filter(Boolean);
          } else if (item.firm_vendor_bank_code) {
            const single = String(item.firm_vendor_bank_code || '').trim();
            if (single) bankCodes = [single];
          }
          return { code, bank_codes: bankCodes };
        })
        .filter(Boolean);
      setVendorCodes(mapped);
    } catch {
      setVendorCodes([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchVendorCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    if (!formName || !formEmail) {
      showToast('error', 'Full name and email are required');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = editUser 
        ? `${API_BASE_URL}/api/users/${editUser.id}`
        : `${API_BASE_URL}/api/users`;
      const method = editUser ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          full_name: formName,
          email: formEmail,
          role: formRole,
          application_code: formApplicationCode,
          mobile_number: formMobileNumber,
          username: formUsername,
          vehicle_number: formVehicleNumber,
          association: formAssociation,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Failed to ${editUser ? 'update' : 'create'} user`);

      showToast('success', `User ${editUser ? 'updated' : 'created'}`);
      
      if (editUser) {
        const updated = data.user;
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
        setEditUser(null);
      } else {
        setUsers((prev) => [data.user, ...prev]);
        setPage(0);
      }

      setFormApplicationCode('');
      setFormMobileNumber('');
      setFormName('');
      setFormUsername('');
      setFormVehicleNumber('');
      setFormEmail('');
      setFormAssociation([]);
      setFormRole('user');
    } catch (e) {
      showToast('error', e.message || `Failed to ${editUser ? 'update' : 'create'} user`);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      return (
        String(u.id).includes(q) ||
        String(u.full_name || '').toLowerCase().includes(q) ||
        String(u.email || '').toLowerCase().includes(q) ||
        String(u.role || '').toLowerCase().includes(q)
      );
    });
  }, [search, users]);

  const paginatedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const confirmDelete = (u) => {
    setUserToDelete(u);
    setDeleteDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setFormName(u.full_name || '');
    setFormEmail(u.email || '');
    setFormRole(u.role || 'user');
    setFormApplicationCode(u.application_code || '');
    setFormMobileNumber(u.mobile_number || '');
    setFormUsername(u.username || '');
    setFormVehicleNumber(u.vehicle_number || '');
    
    let assoc = [];
    if (Array.isArray(u.association)) {
        assoc = u.association;
    } else if (typeof u.association === 'string') {
        try {
            assoc = JSON.parse(u.association);
        } catch {
            assoc = u.association.split(',').map(s => s.trim()).filter(Boolean);
        }
    }
    setFormAssociation(assoc);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditUser(null);
    setFormApplicationCode('');
    setFormMobileNumber('');
    setFormName('');
    setFormUsername('');
    setFormVehicleNumber('');
    setFormEmail('');
    setFormAssociation([]);
    setFormRole('user');
  };

  const openView = (u) => {
    setUserToView(u);
    setViewDialogOpen(true);
  };

  const closeView = () => {
    setViewDialogOpen(false);
    setUserToView(null);
  };

  const openLoginHistory = async (u) => {
    setLoginHistoryUser(u);
    setLoginHistoryDialogOpen(true);
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/login-history/all?userId=${u.id}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setLoginHistory(data.history || []);
      } else {
        showToast('error', data.error || 'Failed to load login history');
      }
    } catch (e) {
      showToast('error', 'Failed to load login history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const closeLoginHistory = () => {
    setLoginHistoryDialogOpen(false);
    setLoginHistoryUser(null);
    setLoginHistory([]);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete user');
      setUsers((prev) => prev.filter((x) => x.id !== userToDelete.id));
      showToast('success', 'User deleted');
    } catch (e) {
      showToast('error', e.message || 'Failed to delete user');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const goToPermissions = (u) => {
    navigate(`/user-permissions/${u.id}`, { state: { user: u } });
  };

  return (
    <Layout>
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          alignItems: 'stretch',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          maxWidth: 1400,
          mx: 'auto',
        }}
      >
        {/* Left: Add User */}
        <Box sx={{ width: { xs: '100%', md: 460 }, flexShrink: 0 }}>
          <Paper
            sx={{
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(124,58,237,0.10)',
                  color: 'secondary.main',
                }}
              >
                <AddIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500, fontSize: 18, lineHeight: 1.2 }}>
                  {editUser ? 'Edit User' : 'Add New User'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {editUser ? 'Update user details' : 'Create a new user account'}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ borderBottomWidth: 1, borderColor: 'black', mt: 1 }} />

            <Box sx={{ p: 2.5 }}>
              {!isSuperAdmin ? (
                <Alert severity="info">
                  Only superadmin can create admin/users.
                </Alert>
              ) : (
                <>
                  <TextField
                    label="Application Code"
                    value={formApplicationCode}
                    onChange={(e) => setFormApplicationCode(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Mobile Number"
                    value={formMobileNumber}
                    onChange={(e) => setFormMobileNumber(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    select
                    label="Designation"
                    value={formRole}
                    onChange={(e) => setFormRole(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    {(roleOptions.length ? roleOptions : ['admin', 'user']).map((r) => (
                      <MenuItem key={r} value={r}>
                        {r.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="UserName (Application Code)"
                    value={formUsername}
                    onChange={(e) => setFormUsername(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Vehical Number"
                    value={formVehicleNumber}
                    onChange={(e) => setFormVehicleNumber(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Association (Vendor Codes)"
                    value={(formAssociation || []).join(', ')}
                    onClick={() => setVendorDialogOpen(true)}
                    fullWidth
                    sx={{ mb: 2 }}
                    InputProps={{
                      readOnly: true,
                    }}
                  />

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {editUser && (
                      <Button variant="outlined" onClick={cancelEdit} disabled={loading} fullWidth sx={{ height: 46 }}>
                        Cancel
                      </Button>
                    )}
                    <Button variant="contained" onClick={handleSubmit} disabled={loading} fullWidth sx={{ height: 46 }}>
                      {editUser ? 'Update User' : 'Create User'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Right: Users List */}
        <Box sx={{ width: { xs: '100%', md: 'auto' }, flexGrow: 1, minWidth: 0 }}>
          <Paper
            sx={{
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{ fontWeight: 500, fontSize: 18 }}>Users List</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  ({filteredUsers.length} users)
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search users"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 260,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark' ? 'rgba(31,41,55,1)' : 'rgba(243,244,246,1)',
                      '& fieldset': { borderColor: 'transparent' },
                      '&:hover fieldset': { borderColor: 'transparent' },
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchUsers}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            <Divider sx={{ borderBottomWidth: 1, borderColor: 'black' }} />

            <Box sx={{ p: 2.5, pt: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          width: 80,
                        }}
                      >
                        ID
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          width: 180,
                        }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        Email
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          width: 140,
                        }}
                      >
                        Role
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          width: 180,
                        }}
                      >
                        Permissions
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          width: 160,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((u) => (
                      <TableRow key={u.id} hover sx={{ '&:last-of-type td': { borderBottom: 'none' } }}>
                        <TableCell>{u.id}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>
                            {u.full_name || (u.email ? u.email.split('@')[0] : '—')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>{u.email}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {u.created_at ? String(u.created_at) : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <RoleChip role={u.role} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: 500 }}>
                              {typeof u.permission_count === 'number' ? u.permission_count : 0}
                            </Typography>
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => goToPermissions(u)}
                              sx={{ textTransform: 'none' }}
                            >
                              View Permissions
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton color="info" size="small" onClick={() => openView(u)} title="View Details">
                              <VisibilityIcon />
                            </IconButton>
                            {isSuperAdmin && (
                              <IconButton color="secondary" size="small" onClick={() => openLoginHistory(u)} title="Login History">
                                <HistoryIcon />
                              </IconButton>
                            )}
                            <IconButton color="primary" size="small" onClick={() => goToPermissions(u)} title="Permissions">
                              <ShieldIcon />
                            </IconButton>
                            {isSuperAdmin && u.role !== 'superadmin' && (
                              <>
                                <IconButton color="primary" size="small" onClick={() => openEdit(u)} title="Edit">
                                  <EditIcon />
                                </IconButton>
                                <IconButton color="error" size="small" onClick={() => confirmDelete(u)} title="Delete">
                                  <DeleteIcon />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loading && paginatedUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ color: 'text.secondary' }}>
                          No users found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 25]}
                count={filteredUsers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </Box>
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={vendorDialogOpen}
        onClose={() => setVendorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Vendor Codes</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mb: 1.5,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setFormAssociation(
                  vendorCodes.map((item) => String(item.code || '').trim()).filter(Boolean)
                )
              }
            >
              Check All
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setFormAssociation([])}
            >
              Uncheck All
            </Button>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(180px, 1fr))', sm: 'repeat(3, minmax(180px, 1fr))' },
              gap: 1,
            }}
          >
            {vendorCodes.map((item) => {
              const code = item.code;
              const checked = formAssociation.indexOf(code) > -1;
              const bankText = (item.bank_codes || []).join(', ');
              return (
                <Box
                  key={code}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => {
                      setFormAssociation((prev) =>
                        prev.indexOf(code) > -1 ? prev.filter((x) => x !== code) : [...prev, code]
                      );
                    }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {code}
                    </Typography>
                    {bankText && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {bankText}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVendorDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete user</DialogTitle>
        <DialogContent>
          Are you sure you want to delete{' '}
          <b>{userToDelete?.email || 'this user'}</b>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog open={viewDialogOpen} onClose={closeView} maxWidth="sm" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent dividers>
          {userToView && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Full Name
                </Typography>
                <Typography variant="body1">{userToView.full_name || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{userToView.email || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Role
                </Typography>
                <RoleChip role={userToView.role} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Application Code
                </Typography>
                <Typography variant="body1">{userToView.application_code || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mobile Number
                </Typography>
                <Typography variant="body1">{userToView.mobile_number || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1">{userToView.username || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Vehicle Number
                </Typography>
                <Typography variant="body1">{userToView.vehicle_number || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Association (Vendor Codes)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {(() => {
                    let assoc = [];
                    if (Array.isArray(userToView.association)) {
                        assoc = userToView.association;
                    } else if (typeof userToView.association === 'string') {
                        try {
                            assoc = JSON.parse(userToView.association);
                        } catch {
                            assoc = userToView.association.split(',').map(s => s.trim()).filter(Boolean);
                        }
                    }
                    if (assoc.length === 0) return <Typography variant="body2">—</Typography>;
                    return assoc.map((code, index) => (
                      <Chip key={index} label={code} size="small" variant="outlined" />
                    ));
                  })()}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeView}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Login History Dialog */}
      <Dialog open={loginHistoryDialogOpen} onClose={closeLoginHistory} maxWidth="lg" fullWidth>
        <DialogTitle>
          Login History - {loginHistoryUser?.full_name || loginHistoryUser?.email || 'User'}
        </DialogTitle>
        <DialogContent dividers>
          {loadingHistory ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Loading login history...</Typography>
            </Box>
          ) : loginHistory.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No login history found.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Device/Browser</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loginHistory.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Typography variant="body2">{formatTime(entry.login_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={entry.ip_address || 'Unknown'} 
                          size="small" 
                          icon={<ComputerIcon />}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {entry.city && entry.country
                              ? `${entry.city}, ${entry.country}`
                              : entry.location || entry.country || 'Unknown'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: 400, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={entry.user_agent || 'Unknown'}
                        >
                          {entry.user_agent || 'Unknown'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLoginHistory}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={toastOpen}
        autoHideDuration={2500}
        onClose={() => setToastOpen(false)}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
